import { FlattenedActionsCsrThemes, useGetAllActions } from "src/api/sure-api/actions/useGetAllActions";
import { ActionsCsr } from "./context";
import { OneActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useGetOneActionPlan";
import { ScoreService, ScoreWithVariation } from "src/services/ScoreService";
import { useActionData } from "./useActionData";
import { ActionPlanData, APScore } from "./useActionPlanData";
import { ActionPlanAction } from "src/api/client-api/ActionPlan/ActionPlanAction/useGetOneActionPlanAction";
import { ActionPlanActionList } from "src/api/client-api/ActionPlan/ActionPlanAction/useGetAllActionPlanAction";
import { ActionsListType, CsrThemeParent } from "src/api/sure-api/actions/useGetOneAction";
import { DimensionValue, NumericalScoreValue } from "src/api/client-api/olapScores/olapScoresSchema";

export type CatalogItem = FlattenedActionsCsrThemes & {
    // It's undefined if isEngaged=false
    actionPlanActionId: string | undefined;
    isEngaged: boolean;

    isCalculable: boolean;

    actionImpact: NumericalScoreValue;
    unit: string | undefined;
};

export type CatalogSubgroup = {
    id: number;
    title: string;
    iconName: string | undefined;

    canHide: boolean;
    engagedActions: number;
    subgroupImpact: NumericalScoreValue;
    unit: string | undefined;
    items: CatalogItem[];
};
export type CatalogByCsr = {
    parent: {
        id: number;
        title: string;
        iconName?: string;
    };
    engagedActions: number;
    totalActions: number;
    canHide: boolean;
    subgroups: CatalogSubgroup[];
};
export type CatalogListByCsr = {
    totalActions: number;
    totalImpact: NumericalScoreValue;
    unit: string | undefined;
    engagedActions: number;
    catalogList: CatalogByCsr[];
};

const getActionsByCsrParents = (
    actions: ActionsListType[] | undefined,
    engagedActions: ActionPlanActionList[],
    lastSelectedVariation: ScoreWithVariation | null,
): CatalogListByCsr => {
    if (actions === undefined)
        return {
            totalActions: 0,
            engagedActions: 0,
            totalImpact: null,
            unit: lastSelectedVariation?.unit,
            catalogList: [],
        };
    const scoreId = lastSelectedVariation?.scoreId;

    const engagedActionsById = engagedActions.reduce(
        (acc, engagedAct) => {
            const actId = engagedAct.action.id;
            acc[actId] = engagedAct;
            return acc;
        },
        {} as Partial<Record<string, ActionPlanActionList>>,
    );

    // Used to gather all csrThemes information and gather them later for display;
    const allCsrInfo = {} as Record<number, CsrThemeParent>;
    const csrKeys = new Set<number>();

    // This makes sure items are duplicated by CsrThemes.
    // They are supposed to be present in only one, BUT, in case they appear in MULTIPLE
    // We want to show them in EVERY CsrThemes.
    const flattenActionsByCsrThemes = actions.flatMap((action) => {
        const newActionList = action.csrThemes.reduce<FlattenedActionsCsrThemes[]>((acc, csrTheme) => {
            const parentCsr = csrTheme.parent;
            if (parentCsr === undefined) {
                return acc;
            }

            if (csrKeys.has(parentCsr.id) === false) {
                allCsrInfo[parentCsr.id] = parentCsr;
            }
            if (csrKeys.has(csrTheme.id) === false) {
                allCsrInfo[csrTheme.id] = {
                    id: csrTheme.id,
                    title: csrTheme.title,
                    iconName: csrTheme.iconName,
                    position: csrTheme.position,
                };
            }

            acc.push({
                ...action,
                csrTheme,
            });
            return acc;
        }, []);
        return newActionList;
    });

    // No Group is excluded because it means bad configuration
    const unknownKey = "noGroup";
    const groupedByParents = Object.groupBy(
        flattenActionsByCsrThemes,
        (action) => action.csrTheme.parent?.id ?? unknownKey,
    );

    const actionIdImpactCounted = new Set<number>();
    const initialActByParents: CatalogListByCsr = {
        totalActions: 0,
        engagedActions: 0,
        totalImpact: null,
        unit: lastSelectedVariation?.unit,
        catalogList: [],
    };

    // Sort parent CSR themes by position
    const sortedParentEntries = Object.entries(groupedByParents).sort(([keyA], [keyB]) => {
        const idA = Number(keyA);
        const idB = Number(keyB);
        const parentA = allCsrInfo[idA];
        const parentB = allCsrInfo[idB];
        const posA = parentA?.position ?? 999999;
        const posB = parentB?.position ?? 999999;
        return posA - posB;
    });

    const actionsByParents = sortedParentEntries.reduce<CatalogListByCsr>((totalAcc, [parentKey, actions]) => {
        const parentId = Number(parentKey);
        const parent = allCsrInfo[parentId];

        if (actions === undefined) {
            // Means group is empty, but it's supposed to be cleared before :)
            return totalAcc;
        }
        const actionsByCsr = Object.groupBy(actions, (action) => action.csrTheme.id);

        const uniqueParentActions = new Set<number>();
        const uniqueParentEngagedActions = new Set<number>();
        // let parentEngagedActionsGroup = 0;

        // Sort child CSR themes by position
        const sortedCsrEntries = Object.entries(actionsByCsr).sort(([keyA], [keyB]) => {
            const idA = Number(keyA);
            const idB = Number(keyB);
            const csrA = allCsrInfo[idA];
            const csrB = allCsrInfo[idB];
            const posA = csrA?.position ?? 999999;
            const posB = csrB?.position ?? 999999;
            return posA - posB;
        });

        const catalogItems = sortedCsrEntries.map(([csrKey, actions]) => {
            const csrId = Number(csrKey);
            const currentCsr = allCsrInfo[csrId];

            let subgroupEngagedActionsGroup = 0;
            let subgroupImpact: NumericalScoreValue = null;
            const refinedActions =
                actions?.map((act) => {
                    const engagedAct = engagedActionsById[act.id];

                    const impact = engagedAct?.totalImpact;
                    const hasImpact = impact !== undefined && Array.isArray(impact) === false && scoreId !== undefined;
                    const totalImpact = hasImpact ? impact[scoreId] : null;

                    const isEngaged = engagedAct !== undefined;
                    // For Subgroups, we want to show the impact of the action everywhere.
                    // But for the grandTotal, we don't want to duplicate the totals.
                    // However, here, if we wanted to make a different calculations we could;
                    subgroupImpact = ScoreService.addScore(subgroupImpact, totalImpact);
                    if (actionIdImpactCounted.has(act.id) === false) {
                        totalAcc.totalImpact = ScoreService.addScore(totalAcc.totalImpact, totalImpact);
                        totalAcc.totalActions += 1;

                        if (isEngaged) {
                            totalAcc.engagedActions += 1;
                        }

                        actionIdImpactCounted.add(act.id);
                    }

                    uniqueParentActions.add(act.id);
                    if (isEngaged) {
                        uniqueParentEngagedActions.add(act.id);
                        subgroupEngagedActionsGroup += 1;
                    }

                    const isCalculable = act.actionFields.length > 0;

                    const item: CatalogItem = {
                        actionPlanActionId: engagedAct?.id,
                        isEngaged,
                        isCalculable,
                        actionImpact: totalImpact,
                        unit: lastSelectedVariation?.unit,
                        ...act,
                    };
                    return item;
                }) ?? [];

            const canHide = subgroupEngagedActionsGroup === refinedActions.length;
            const refinedAct: CatalogSubgroup = {
                id: currentCsr.id,
                title: currentCsr.title,
                iconName: currentCsr.iconName,

                subgroupImpact,
                unit: lastSelectedVariation?.unit,
                canHide,
                engagedActions: subgroupEngagedActionsGroup,
                items: refinedActions,
            };
            return refinedAct;
        });

        const canHideParent = uniqueParentEngagedActions.size === uniqueParentActions.size;
        totalAcc.catalogList.push({
            parent,
            engagedActions: uniqueParentEngagedActions.size,
            totalActions: uniqueParentActions.size,
            canHide: canHideParent,
            subgroups: catalogItems,
        });

        return totalAcc;
    }, initialActByParents);

    return actionsByParents;
};

export type ActionScores = {
    start: ActionScoreByCsr;
    end: ActionScoreByCsr;
};

export type ActionScoreByCsr = Partial<Record<string, ActionsCsr>>;

export type PostScoresToCompare = {
    start: {
        scores: DimensionValue[];
        post: APScore | undefined;
    };
    target: {
        scores: DimensionValue[];
        post: APScore | undefined;
    };
    posts: APScore[];
};

type Options = {
    ssqVersionId: number | undefined;
    actionPlan: OneActionPlan | undefined;
    engagedActions: ActionPlanActionList[] | undefined;
    lastSelectedVariation: ScoreWithVariation | null;
};

export const useActionScore = (options: Options) => {
    const { ssqVersionId, actionPlan, engagedActions = [], lastSelectedVariation } = options;

    // Request Sure Side.
    // Possibly done tomorrow by Backend;
    const { actions, getActionsByCsrTheme, isReady } = useGetAllActions({ ssqVersionId });
    const actionsByCsrTheme = getActionsByCsrTheme();
    const actionsByParents = getActionsByCsrParents(actions, engagedActions, lastSelectedVariation);

    const { getPostsForAction } = useActionData({ actionPlan });

    const selectPostsByCampaigns = (
        plan: ActionPlanData | undefined,
        startingCampaign: number | undefined,
        targetCampaign: number | undefined,
        includeStart?: boolean,
    ): PostScoresToCompare => {
        const postsForAction = getPostsForAction(plan, startingCampaign, targetCampaign, includeStart);

        const postsScoreStart = ScoreService.getScoreProperties(postsForAction.foundStartPost?.score2);
        const postsScoreTarget = ScoreService.getScoreProperties(postsForAction.foundEndPost?.score2);
        return {
            start: {
                scores: postsScoreStart,
                post: postsForAction.foundStartPost,
            },
            target: {
                scores: postsScoreTarget,
                post: postsForAction.foundEndPost,
            },
            posts: postsForAction.allPosts,
        };
    };

    const selectPostsByActionPlanAction = (plan: ActionPlanData, action: ActionPlanAction): PostScoresToCompare => {
        const startingCampaign = action.startSimulationSsqPost.campaign?.id;
        const targetCampaign = action.endSimulationSsqPost.campaign?.id;

        return selectPostsByCampaigns(plan, startingCampaign, targetCampaign, true);
    };

    return {
        isReady,
        actions,
        actionsByCsrTheme,
        actionsByParents,
        selectPostsByActionPlanAction,
    };
};
